import { useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Flex, Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  ActionBar,
  Alert,
  Button,
  EmptyState,
  Filters,
  LoadingState,
  Pagination,
  Search,
  SectionHeader,
  Select,
  StatusChip,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useToast,
} from "@/components/ui";
import { apiErrorMessage } from "@/api/client";
import { useApiClient } from "@/api/useApiClient";
import { usePageBreadcrumbs } from "@/shell/usePageBreadcrumbs";
import { useHubEmbedded } from "@/modules/hub-kit/HubKit";
import { createFinanceApi } from "../api";
import { formatLabel, formatMoney, moneyTone } from "../labels";
import { useFinanceQueue } from "../useFinanceQueue";
import type {
  CommissionRuleDto,
  DisputeDto,
  ExceptionDto,
  PayoutDto,
  RefundDto,
  SettlementDto,
  TransactionDto,
} from "../types";
import {
  CreateAdjustmentDialog,
  CreateCommissionDialog,
  CreateDisputeDialog,
  CreateExceptionDialog,
  CreatePayoutDialog,
  CreateRefundDialog,
  CreateSettlementDialog,
  CreateTransactionDialog,
} from "../components/FinanceDialogs";

function QueueShell({
  title,
  description,
  statuses,
  sortOptions,
  defaultSort,
  columns,
  rows,
  loading,
  error,
  searchDraft,
  setSearchDraft,
  status,
  sort,
  page,
  nextCursor,
  onStatus,
  onSort,
  onSearch,
  onNext,
  onPrevious,
  onRefresh,
  onCreate,
  createLabel,
}: {
  title: string;
  description: string;
  statuses: string[];
  sortOptions: { value: string; label: string }[];
  defaultSort: string;
  columns: string[];
  rows: ReactNode;
  loading: boolean;
  error: string | null;
  searchDraft: string;
  setSearchDraft: (value: string) => void;
  status: string;
  sort: string;
  page: number;
  nextCursor: string | null;
  onStatus: (value: string) => void;
  onSort: (value: string) => void;
  onSearch: () => void;
  onNext: () => void;
  onPrevious: () => void;
  onRefresh: () => void;
  onCreate: () => void;
  createLabel: string;
}) {
  const embedded = useHubEmbedded();
  usePageBreadcrumbs(
    embedded
      ? embedded.crumbs
      : [
          { label: "HUDDL HQ", to: "/app" },
          { label: "Finance", to: "/app/finance" },
          { label: title },
        ],
  );

  return (
    <Stack gap={24}>
      <SectionHeader
        title={title}
        {...(embedded ? {} : { description })}
        actions={
          <Flex gap={8}>
            <Button variant="secondary" onClick={onCreate}>
              {createLabel}
            </Button>
            <Button variant="secondary" onClick={onRefresh}>
              Refresh
            </Button>
          </Flex>
        }
      />
      <Filters>
        <div style={{ flex: "1 1 240px", minWidth: 200 }}>
          <Search
            value={searchDraft}
            onValueChange={setSearchDraft}
            placeholder="Search"
            onKeyDown={(event) => {
              if (event.key === "Enter") onSearch();
            }}
          />
        </div>
        <Select
          label="Status"
          value={status}
          onChange={(event) => onStatus(event.target.value)}
          options={[
            { value: "", label: "All statuses" },
            ...statuses.map((item) => ({
              value: item,
              label: formatLabel(item),
            })),
          ]}
        />
        <Select
          label="Sort"
          value={sort || defaultSort}
          onChange={(event) => onSort(event.target.value)}
          options={sortOptions}
        />
        <Button variant="primary" onClick={onSearch}>
          Apply search
        </Button>
      </Filters>
      {error ? (
        <Alert tone="danger" title={`Unable to load ${title}`}>
          {error}
        </Alert>
      ) : null}
      {loading ? <LoadingState message={`Loading ${title}…`} /> : null}
      {!loading && !error && !rows ? (
        <EmptyState title={`No ${title} found`} reason="none" />
      ) : null}
      {!loading && rows ? (
        <Stack gap={12}>
          <ActionBar
            left={
              <Text role="small" color="secondary" as="span">
                Page {page}
              </Text>
            }
            right={
              <Pagination
                page={page}
                pageCount={nextCursor ? page + 1 : page}
                onPageChange={(nextPage) => {
                  if (nextPage > page) onNext();
                  if (nextPage < page) onPrevious();
                }}
              />
            }
          />
          <Table>
            <Thead>
              <Tr>
                {columns.map((column) => (
                  <Th key={column}>{column}</Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>{rows}</Tbody>
          </Table>
        </Stack>
      ) : null}
    </Stack>
  );
}

export function TransactionsQueuePage() {
  const client = useApiClient();
  const api = useMemo(() => createFinanceApi(client), [client]);
  const navigate = useNavigate();
  const toast = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const loadPage = useMemo(
    () =>
      async (params: { cursor?: string; q: string; status: string; sort: string }) => {
        const list = await api.listTransactions({
          limit: 25,
          ...(params.cursor ? { cursor: params.cursor } : {}),
          ...(params.q ? { q: params.q } : {}),
          ...(params.status ? { status: params.status } : {}),
          sort: params.sort || "-occurred_at",
        });
        return { items: list.transactions, pagination: list.pagination };
      },
    [api],
  );
  const queue = useFinanceQueue<TransactionDto>(loadPage);

  return (
    <>
      <QueueShell
        title="Transactions"
        description="Money movement records. Booking status is unchanged here."
        statuses={[
          "initiated",
          "pending",
          "succeeded",
          "failed",
          "reversed",
          "refunded_partial",
          "refunded_full",
          "disputed",
          "settled",
          "reconciled",
        ]}
        defaultSort="-occurred_at"
        sortOptions={[
          { value: "-occurred_at", label: "Occurred (newest)" },
          { value: "occurred_at", label: "Occurred (oldest)" },
          { value: "amount", label: "Amount" },
          { value: "status", label: "Status" },
        ]}
        columns={["ID", "Type", "Status", "Amount", "Occurred", "Actions"]}
        rows={
          queue.rows.length === 0
            ? null
            : queue.rows.map((row) => (
                <Tr key={row.id}>
                  <Td>{row.id}</Td>
                  <Td>{formatLabel(row.transaction_type)}</Td>
                  <Td>
                    <StatusChip tone={moneyTone(row.status)}>
                      {formatLabel(row.status)}
                    </StatusChip>
                  </Td>
                  <Td>{formatMoney(row.amount, row.currency)}</Td>
                  <Td>{row.occurred_at}</Td>
                  <Td>
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() =>
                        void navigate(`/app/finance/transactions/${row.id}`)
                      }
                    >
                      Open
                    </Button>
                  </Td>
                </Tr>
              ))
        }
        loading={queue.loading}
        error={queue.error}
        searchDraft={queue.searchDraft}
        setSearchDraft={queue.setSearchDraft}
        status={queue.status}
        sort={queue.sort}
        page={queue.page}
        nextCursor={queue.nextCursor}
        onStatus={queue.setStatus}
        onSort={queue.setSort}
        onSearch={queue.applySearch}
        onNext={queue.goNext}
        onPrevious={queue.goPrevious}
        onRefresh={() => void queue.load()}
        onCreate={() => setCreateOpen(true)}
        createLabel="Record Transaction"
      />
      <Flex gap={8} style={{ marginTop: -8 }}>
        <Button variant="ghost" size="sm" onClick={() => setAdjustOpen(true)}>
          Create adjustment instead
        </Button>
      </Flex>
      <CreateTransactionDialog
        open={createOpen}
        busy={busy}
        error={formError}
        onClose={() => {
          setCreateOpen(false);
          setFormError(null);
        }}
        onSubmit={async (body) => {
          setBusy(true);
          setFormError(null);
          try {
            const result = await api.createTransaction(body);
            toast.push({ title: "Transaction recorded", tone: "success" });
            setCreateOpen(false);
            void navigate(`/app/finance/transactions/${result.transaction.id}`);
          } catch (err) {
            setFormError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
      <CreateAdjustmentDialog
        open={adjustOpen}
        busy={busy}
        error={formError}
        onClose={() => {
          setAdjustOpen(false);
          setFormError(null);
        }}
        onSubmit={async (body) => {
          setBusy(true);
          setFormError(null);
          try {
            const result = await api.createAdjustment(body);
            toast.push({ title: "Adjustment recorded", tone: "success" });
            setAdjustOpen(false);
            void navigate(`/app/finance/transactions/${result.transaction.id}`);
          } catch (err) {
            setFormError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
    </>
  );
}

function makeSimpleQueue<T extends { id: string; status: string }>(config: {
  title: string;
  description: string;
  statuses: string[];
  defaultSort: string;
  sortOptions: { value: string; label: string }[];
  columns: string[];
  path: string;
  createLabel: string;
  load: (
    api: ReturnType<typeof createFinanceApi>,
    params: { cursor?: string; q: string; status: string; sort: string },
  ) => Promise<{ items: T[]; pagination: { limit: number; next_cursor: string | null } }>;
  renderRow: (row: T, open: () => void) => ReactNode;
  CreateDialog: typeof CreateRefundDialog;
  create: (
    api: ReturnType<typeof createFinanceApi>,
    body: Record<string, unknown>,
  ) => Promise<{ id: string }>;
}) {
  return function QueuePage() {
    const client = useApiClient();
    const api = useMemo(() => createFinanceApi(client), [client]);
    const navigate = useNavigate();
    const toast = useToast();
    const [createOpen, setCreateOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);
    const loadPage = useMemo(
      () =>
        (params: { cursor?: string; q: string; status: string; sort: string }) =>
          config.load(api, params),
      [api],
    );
    const CreateDialog = config.CreateDialog;
    const queue = useFinanceQueue<T>(loadPage);

    return (
      <>
        <QueueShell
          title={config.title}
          description={config.description}
          statuses={config.statuses}
          defaultSort={config.defaultSort}
          sortOptions={config.sortOptions}
          columns={config.columns}
          rows={
            queue.rows.length === 0
              ? null
              : queue.rows.map((row) =>
                  config.renderRow(row, () =>
                    void navigate(`${config.path}/${row.id}`),
                  ),
                )
          }
          loading={queue.loading}
          error={queue.error}
          searchDraft={queue.searchDraft}
          setSearchDraft={queue.setSearchDraft}
          status={queue.status}
          sort={queue.sort}
          page={queue.page}
          nextCursor={queue.nextCursor}
          onStatus={queue.setStatus}
          onSort={queue.setSort}
          onSearch={queue.applySearch}
          onNext={queue.goNext}
          onPrevious={queue.goPrevious}
          onRefresh={() => void queue.load()}
          onCreate={() => setCreateOpen(true)}
          createLabel={config.createLabel}
        />
        <CreateDialog
          open={createOpen}
          busy={busy}
          error={formError}
          onClose={() => {
            setCreateOpen(false);
            setFormError(null);
          }}
          onSubmit={async (body) => {
            setBusy(true);
            setFormError(null);
            try {
              const created = await config.create(api, body);
              toast.push({ title: `${config.title} created`, tone: "success" });
              setCreateOpen(false);
              void navigate(`${config.path}/${created.id}`);
            } catch (err) {
              setFormError(apiErrorMessage(err));
            } finally {
              setBusy(false);
            }
          }}
        />
      </>
    );
  };
}

export const RefundsQueuePage = makeSimpleQueue<RefundDto>({
  title: "Refunds",
  description: "Requester ≠ approver ≠ issuer. Dual Approval for amount ≥ 1000.",
  statuses: [
    "requested",
    "under_review",
    "approved",
    "rejected",
    "executed",
    "settled",
    "failed",
    "requires_intervention",
  ],
  defaultSort: "-requested_at",
  sortOptions: [
    { value: "-requested_at", label: "Requested (newest)" },
    { value: "amount", label: "Amount" },
    { value: "status", label: "Status" },
  ],
  columns: ["ID", "Status", "Amount", "Requested", "Actions"],
  path: "/app/finance/refunds",
  createLabel: "Request Refund",
  load: async (api, params) => {
    const list = await api.listRefunds({
      limit: 25,
      sort: params.sort || "-requested_at",
      ...(params.cursor ? { cursor: params.cursor } : {}),
      ...(params.q ? { q: params.q } : {}),
      ...(params.status ? { status: params.status } : {}),
    });
    return { items: list.refunds, pagination: list.pagination };
  },
  renderRow: (row, open) => (
    <Tr key={row.id}>
      <Td>{row.id}</Td>
      <Td>
        <StatusChip tone={moneyTone(row.status)}>{formatLabel(row.status)}</StatusChip>
      </Td>
      <Td>{row.amount}</Td>
      <Td>{row.requested_at}</Td>
      <Td>
        <Button size="sm" variant="primary" onClick={open}>
          Open
        </Button>
      </Td>
    </Tr>
  ),
  CreateDialog: CreateRefundDialog,
  create: async (api, body) => {
    const result = await api.requestRefund(body);
    return { id: result.refund.id };
  },
});

export const PayoutsQueuePage = makeSimpleQueue<PayoutDto>({
  title: "Payouts",
  description: "Business XOR Creator payee. Dual Approval for material hold/release.",
  statuses: [
    "eligible",
    "pending_review",
    "approved",
    "processing",
    "paid",
    "settled",
    "reconciled",
    "held",
    "delayed",
    "failed",
    "disputed",
  ],
  defaultSort: "id",
  sortOptions: [
    { value: "status", label: "Status" },
    { value: "id", label: "ID" },
  ],
  columns: ["ID", "Status", "Amount", "Payee", "Actions"],
  path: "/app/finance/payouts",
  createLabel: "Create Payout",
  load: async (api, params) => {
    const list = await api.listPayouts({
      limit: 25,
      sort: params.sort || "id",
      ...(params.cursor ? { cursor: params.cursor } : {}),
      ...(params.q ? { q: params.q } : {}),
      ...(params.status ? { status: params.status } : {}),
    });
    return { items: list.payouts, pagination: list.pagination };
  },
  renderRow: (row, open) => (
    <Tr key={row.id}>
      <Td>{row.id}</Td>
      <Td>
        <StatusChip tone={moneyTone(row.status)}>{formatLabel(row.status)}</StatusChip>
      </Td>
      <Td>{formatMoney(row.amount, row.currency)}</Td>
      <Td>{row.business_id ?? row.creator_profile_id ?? "—"}</Td>
      <Td>
        <Button size="sm" variant="primary" onClick={open}>
          Open
        </Button>
      </Td>
    </Tr>
  ),
  CreateDialog: CreatePayoutDialog,
  create: async (api, body) => {
    const result = await api.createPayout(body);
    return { id: result.payout.id };
  },
});

export const SettlementsQueuePage = makeSimpleQueue<SettlementDto>({
  title: "Settlements",
  description: "Reconcile expected versus settled payout amounts.",
  statuses: ["pending", "settled", "reconciled", "unmatched", "failed"],
  defaultSort: "id",
  sortOptions: [
    { value: "status", label: "Status" },
    { value: "id", label: "ID" },
  ],
  columns: ["ID", "Status", "Expected", "Settled", "Actions"],
  path: "/app/finance/settlements",
  createLabel: "Create Settlement",
  load: async (api, params) => {
    const list = await api.listSettlements({
      limit: 25,
      sort: params.sort || "id",
      ...(params.cursor ? { cursor: params.cursor } : {}),
      ...(params.q ? { q: params.q } : {}),
      ...(params.status ? { status: params.status } : {}),
    });
    return { items: list.settlements, pagination: list.pagination };
  },
  renderRow: (row, open) => (
    <Tr key={row.id}>
      <Td>{row.id}</Td>
      <Td>
        <StatusChip tone={moneyTone(row.status)}>{formatLabel(row.status)}</StatusChip>
      </Td>
      <Td>{row.expected_amount}</Td>
      <Td>{row.settled_amount ?? "—"}</Td>
      <Td>
        <Button size="sm" variant="primary" onClick={open}>
          Open
        </Button>
      </Td>
    </Tr>
  ),
  CreateDialog: CreateSettlementDialog,
  create: async (api, body) => {
    const result = await api.createSettlement(body);
    return { id: result.settlement.id };
  },
});

export const CommissionRulesQueuePage = makeSimpleQueue<CommissionRuleDto>({
  title: "Commission Rules",
  description: "Super Admin approval activates a rule and replaces the prior active rule.",
  statuses: ["draft", "approved", "scheduled", "active", "replaced", "retired"],
  defaultSort: "-effective_from",
  sortOptions: [
    { value: "-effective_from", label: "Effective (newest)" },
    { value: "status", label: "Status" },
  ],
  columns: ["ID", "Scope", "Status", "Effective from", "Actions"],
  path: "/app/finance/commission-rules",
  createLabel: "Create Rule",
  load: async (api, params) => {
    const list = await api.listCommissionRules({
      limit: 25,
      sort: params.sort || "-effective_from",
      ...(params.cursor ? { cursor: params.cursor } : {}),
      ...(params.q ? { q: params.q } : {}),
      ...(params.status ? { status: params.status } : {}),
    });
    return { items: list.commission_rules, pagination: list.pagination };
  },
  renderRow: (row, open) => (
    <Tr key={row.id}>
      <Td>{row.id}</Td>
      <Td>
        {row.rule_scope_type} / {row.rule_scope_id}
      </Td>
      <Td>
        <StatusChip tone={moneyTone(row.status)}>{formatLabel(row.status)}</StatusChip>
      </Td>
      <Td>{row.effective_from}</Td>
      <Td>
        <Button size="sm" variant="primary" onClick={open}>
          Open
        </Button>
      </Td>
    </Tr>
  ),
  CreateDialog: CreateCommissionDialog,
  create: async (api, body) => {
    const result = await api.createCommissionRule(body);
    return { id: result.commission_rule.id };
  },
});

export const ExceptionsQueuePage = makeSimpleQueue<ExceptionDto>({
  title: "Financial Exceptions",
  description: "Operational money exceptions. Distinct from Financial Disputes.",
  statuses: [
    "detected",
    "investigating",
    "awaiting_information",
    "resolved",
    "escalated",
    "closed",
  ],
  defaultSort: "-detected_at",
  sortOptions: [
    { value: "-detected_at", label: "Detected (newest)" },
    { value: "severity", label: "Severity" },
    { value: "status", label: "Status" },
  ],
  columns: ["ID", "Type", "Severity", "Status", "Actions"],
  path: "/app/finance/exceptions",
  createLabel: "Open Exception",
  load: async (api, params) => {
    const list = await api.listExceptions({
      limit: 25,
      sort: params.sort || "-detected_at",
      ...(params.cursor ? { cursor: params.cursor } : {}),
      ...(params.q ? { q: params.q } : {}),
      ...(params.status ? { status: params.status } : {}),
    });
    return { items: list.exceptions, pagination: list.pagination };
  },
  renderRow: (row, open) => (
    <Tr key={row.id}>
      <Td>{row.id}</Td>
      <Td>{formatLabel(row.exception_type)}</Td>
      <Td>
        <StatusChip tone={moneyTone(row.severity)}>{formatLabel(row.severity)}</StatusChip>
      </Td>
      <Td>
        <StatusChip tone={moneyTone(row.status)}>{formatLabel(row.status)}</StatusChip>
      </Td>
      <Td>
        <Button size="sm" variant="primary" onClick={open}>
          Open
        </Button>
      </Td>
    </Tr>
  ),
  CreateDialog: CreateExceptionDialog,
  create: async (api, body) => {
    const result = await api.createException(body);
    return { id: result.exception.id };
  },
});

export const DisputesQueuePage = makeSimpleQueue<DisputeDto>({
  title: "Financial Disputes",
  description: "Money disputes. Separate from Exceptions and from Trust & Safety cases.",
  statuses: [
    "detected",
    "investigating",
    "awaiting_information",
    "resolved",
    "escalated",
    "closed",
  ],
  defaultSort: "-opened_at",
  sortOptions: [
    { value: "-opened_at", label: "Opened (newest)" },
    { value: "status", label: "Status" },
  ],
  columns: ["ID", "Status", "Opened", "Actions"],
  path: "/app/finance/disputes",
  createLabel: "Open Dispute",
  load: async (api, params) => {
    const list = await api.listDisputes({
      limit: 25,
      sort: params.sort || "-opened_at",
      ...(params.cursor ? { cursor: params.cursor } : {}),
      ...(params.q ? { q: params.q } : {}),
      ...(params.status ? { status: params.status } : {}),
    });
    return { items: list.disputes, pagination: list.pagination };
  },
  renderRow: (row, open) => (
    <Tr key={row.id}>
      <Td>{row.id}</Td>
      <Td>
        <StatusChip tone={moneyTone(row.status)}>{formatLabel(row.status)}</StatusChip>
      </Td>
      <Td>{row.opened_at}</Td>
      <Td>
        <Button size="sm" variant="primary" onClick={open}>
          Open
        </Button>
      </Td>
    </Tr>
  ),
  CreateDialog: CreateDisputeDialog,
  create: async (api, body) => {
    const result = await api.openDispute(body);
    return { id: result.dispute.id };
  },
});
